import { createExperimentalZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { SteppedDialogFormProps, useSteppedDialogForm, useSteppedDialogFormControls } from '@mews-commander/form';
import { OptimusProvider } from '@mews-commander/ui';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { useDetailsSchema } from './schemas/detailsSchema';
import { AllowanceFormStep } from './types';
import { DetailsStepFields } from './steps/DetailsStepFields';
import {
    AllowanceFormValues,
    useAllowanceCreateFormDefaultValues,
    useToAllowanceAddParameters,
} from './schemas/allowanceFormSchema';
import { allowancePricingSchema } from './schemas/pricingSchema';
import { AllowancePricingStepFields } from './steps/AllowancePricingStepFields';
import { AllowanceOptionsStepFields } from './steps/AllowanceOptionsStepFields';
import { AllowanceAccountingStepFields } from './steps/AllowanceAccountingStepFields';
import { allowanceOptionsSchema } from './schemas/optionsSchema';

export const useAllowanceDialogForm = () =>
    useSteppedDialogForm<AllowanceFormStep>({
        id: 'allowanceForm',
        title: lt('AllowanceFormTitleCreateAllowance'),
        steps: ['details', 'pricing', 'options', 'accounting'],
        saveLabel: lt('Create'),
        stepLabels: {
            details: lt('DetailPlural'),
            pricing: lt('Pricing'),
            options: lt('Options'),
            accounting: lt('Accounting'),
        },
        renderForm: (props) => (
            <OptimusProvider density="spacious">
                <AllowanceForm {...props} />
            </OptimusProvider>
        ),
    });

export const AllowanceForm = (props: SteppedDialogFormProps<AllowanceFormStep>) => {
    const detailsSchema = useDetailsSchema();

    const mutation = useRepositoryMutation('allowances', 'add', {
        invalidateQueries: { products: ['getAll', 'getOverviewEntries'] },
        onSuccess: () => {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        },
    });

    const toAllowanceAddParameters = useToAllowanceAddParameters();

    const { renderFormContent } = useSteppedDialogFormControls<AllowanceFormStep, AllowanceFormValues>({
        ...props,
        stepResolvers: {
            details: createExperimentalZodFormResolver(detailsSchema),
            pricing: createExperimentalZodFormResolver(allowancePricingSchema),
            options: createExperimentalZodFormResolver(allowanceOptionsSchema),
        },
        defaultValues: useAllowanceCreateFormDefaultValues(),
        onSubmit: async (formValues) => {
            const requestParameters = await toAllowanceAddParameters(formValues);
            await mutation.mutateAsync(requestParameters);
        },
    });

    return renderFormContent((step) => {
        switch (step) {
            case 'details':
                return (
                    <DetailsStepFields
                        enabledExternalName={false}
                        enabledUnits={false}
                        helpers={{
                            descriptions: lt('AllowanceFormInputHintDescription'),
                            image: lt('AllowanceFormInputHintImage'),
                            externalIdentifier: lt('AllowanceFormInputHintExternalIdentifier'),
                            names: lt('AllowanceFormInputHintName'),
                        }}
                    />
                );
            case 'pricing':
                return <AllowancePricingStepFields />;
            case 'options':
                return <AllowanceOptionsStepFields />;
            case 'accounting':
                return <AllowanceAccountingStepFields />;
        }
    });
};
